/**
 * Calculates the combined height of sticky headers that could be covering content.
 * This includes the TopActionBar and PersonObjectHeading (or other sticky headers).
 *
 * @returns The total height in pixels of all sticky headers
 */
export function calculateStickyHeaderHeight(): number {
  const topActionBar = document.querySelector('[data-testid="top-action-bar"]') as HTMLElement;
  const personHeader = document.querySelector('[data-testid="person-details-header"]');

  let headerOffset = 0;

  if (personHeader) {
    let current: HTMLElement | null = personHeader.parentElement;
    while (current) {
      const computedStyle = window.getComputedStyle(current);
      if (computedStyle.position === "sticky" && computedStyle.top !== "auto") {
        headerOffset = current.offsetHeight;
        break;
      }
      current = current.parentElement;
    }
  }

  if (topActionBar) {
    const topActionBarStyle = window.getComputedStyle(topActionBar);
    if (topActionBarStyle.position === "sticky" || topActionBarStyle.position === "fixed") {
      headerOffset += topActionBar.offsetHeight;
    }
  }

  if (headerOffset === 0) {
    headerOffset = 120;
  }

  return headerOffset;
}

/**
 * Scrolls to an element by ID or data-testid, accounting for sticky headers.
 *
 * @param targetId - The ID or data-testid of the element to scroll to
 * @param additionalOffset - Additional offset in pixels (default: 16px = 1rem)
 * @param behavior - Scroll behavior (default: "smooth")
 */
export function scrollToElement(
  targetId: string,
  additionalOffset: number = 16,
  behavior: ScrollBehavior = "smooth"
): void {
  const element = document.getElementById(targetId) || document.querySelector(`[data-testid="${targetId}"]`);
  if (!element) {
    return;
  }

  const headerOffset = calculateStickyHeaderHeight();
  const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
  const offsetPosition = elementPosition - headerOffset - additionalOffset;

  window.scrollTo({
    top: offsetPosition,
    behavior
  });
}
